import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDocumentGeneration } from "@/hooks/useDocumentGeneration";
import { useMessageHandling } from "@/hooks/ai-assistant/useMessageHandling";
import { useFileAnalysisHandler } from "@/hooks/ai-assistant/useFileAnalysis";
import { useCaseCreation } from "@/hooks/ai-assistant/useCaseCreation";
import { useDataProcessingTracking } from "@/hooks/ai-assistant/useDataProcessingTracking";
import { useChatArchive } from "@/hooks/ai-assistant/useChatArchive";
import { Message } from "@/types/chat";
import { useToast } from "@/hooks/use-toast";

const LAST_CHAT_KEY = 'lastChatSession';

export function useAIAssistant() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [showRestorePrompt, setShowRestorePrompt] = useState(false);
  
  const { generateDocument, isGenerating, progress: docProgress } = useDocumentGeneration();
  const { messages, userInput, setUserInput, isLoading, handleSendMessage, setMessages } = useMessageHandling();
  const { isAnalyzing, handleFileAnalysis } = useFileAnalysisHandler();
  const { isCreatingCase, progress, handleCreateCase } = useCaseCreation();
  const { logDataProcessing } = useDataProcessingTracking();
  const { 
    savedChats,
    handleSaveChat,
    loadChat,
    handleDeleteChat,
    handleRenameChat,
    resetChat
  } = useChatArchive();

  // Check for saved chat session on mount
  useEffect(() => {
    const savedChat = localStorage.getItem(LAST_CHAT_KEY);
    if (savedChat) {
      const parsedChat = JSON.parse(savedChat);
      if (parsedChat.messages && parsedChat.messages.length > 1) {
        setShowRestorePrompt(true);
      }
    }
  }, []);

  // Save chat session whenever messages change
  useEffect(() => {
    if (messages.length > 1) {
      localStorage.setItem(LAST_CHAT_KEY, JSON.stringify({
        messages,
        timestamp: new Date().toISOString()
      }));
    }
  }, [messages]);

  const handleRestoreChat = () => {
    const savedChat = localStorage.getItem(LAST_CHAT_KEY);
    if (savedChat) {
      const { messages: savedMessages } = JSON.parse(savedChat);
      setMessages(savedMessages);
      setShowRestorePrompt(false);
      toast({
        title: "Chat gjenopprettet",
        description: "Din forrige samtale er nå lastet inn.",
      });
    }
  };

  const handleStartNewChat = () => {
    localStorage.removeItem(LAST_CHAT_KEY);
    setMessages([{
      id: "initial",
      role: "assistant",
      content: "Velkommen til AdvoKAI! Hvordan kan jeg hjelpe deg i dag?",
      created_at: new Date().toISOString()
    }]);
    setShowRestorePrompt(false);
    toast({
      title: "Ny chat startet",
      description: "Du kan nå starte en ny samtale.",
    });
  };

  const handleCreateDocument = async () => {
    const title = "AI Generated Document";
    const content = messages.map(m => m.content).join('\n\n');
    await logDataProcessing(
      'document_generation',
      'Generate document from chat conversation',
      { messageCount: messages.length }
    );
    await generateDocument(title, content);
  };

  const handleLawSearch = async () => {
    await logDataProcessing(
      'law_search',
      'Search legal references based on chat context',
      { messageCount: messages.length }
    );
    localStorage.setItem('aiAssistantContext', JSON.stringify(messages));
    navigate('/search');
  };

  const enhancedHandleSendMessage = async () => {
    await logDataProcessing(
      'chat_message',
      'Process user message for AI response',
      { messageLength: userInput.length }
    );
    await handleSendMessage(currentConversationId);
  };

  const enhancedHandleFileAnalysis = async (file: File) => {
    await logDataProcessing(
      'file_analysis',
      'Analyze uploaded file content',
      { fileName: file.name, fileSize: file.size }
    );
    await handleFileAnalysis(file, currentConversationId, setMessages);
  };

  const enhancedHandleCreateCase = async () => {
    await logDataProcessing(
      'case_creation',
      'Create new case from chat conversation',
      { messageCount: messages.length }
    );
    await handleCreateCase(messages);
  };

  return {
    messages,
    userInput,
    setUserInput,
    isLoading,
    isCreatingCase,
    isGenerating,
    isAnalyzing,
    progress,
    docProgress,
    showRestorePrompt,
    handleSendMessage: enhancedHandleSendMessage,
    handleCreateDocument,
    handleCreateCase: enhancedHandleCreateCase,
    handleLawSearch,
    handleFileAnalysis: (file: File) => enhancedHandleFileAnalysis(file),
    handleSaveChat,
    savedChats,
    loadChat,
    handleDeleteChat,
    handleRenameChat,
    resetChat,
    setMessages,
    handleRestoreChat,
    handleStartNewChat
  };
}